/*
  Perspective MTL · site behaviour (always on, independent of motion):
  header scroll state, Services dropdowns, the mobile menu sheet, the
  on-demand booking scheduler and the contact form honeypot.
*/
(() => {
  const root = document.documentElement;
  root.classList.remove('no-js');

  /* ---------- Header scroll state ---------- */
  const header = document.querySelector('[data-header]');
  const sentinel = document.querySelector('[data-header-sentinel]');
  if (header && sentinel && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      header.classList.toggle('is-scrolled', !entry.isIntersecting);
    }).observe(sentinel);
  }

  /* ---------- Services dropdowns: Esc and outside click close them ---------- */
  const dropdowns = document.querySelectorAll('[data-dropdown]');
  document.addEventListener('click', (event) => {
    dropdowns.forEach((d) => { if (d.open && !d.contains(event.target)) d.open = false; });
  });
  dropdowns.forEach((d) => d.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && d.open) {
      d.open = false;
      d.querySelector('summary').focus();
    }
  }));

  /* ---------- Mobile menu sheet ---------- */
  const menu = document.querySelector('[data-menu]');
  if (menu && header) {
    const toggle = menu.querySelector('summary');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');

    const close = () => {
      if (!menu.open || menu.classList.contains('is-closing')) return;
      if (reduced.matches) { menu.open = false; return; }
      menu.classList.add('is-closing');
      menu.querySelector('.menu__sheet').addEventListener('animationend', () => {
        menu.classList.remove('is-closing');
        menu.open = false;
      }, { once: true });
    };

    toggle.addEventListener('click', (event) => {
      if (menu.open) { event.preventDefault(); close(); }
    });

    menu.addEventListener('toggle', () => {
      root.classList.toggle('menu-open', menu.open);
      if (menu.open) {
        const first = menu.querySelector('.menu__link');
        if (first) first.focus();
      } else {
        toggle.focus();
      }
    });

    // Focus stays within the header bar and the sheet while the menu is open
    header.addEventListener('keydown', (event) => {
      if (!menu.open) return;
      if (event.key === 'Escape') { close(); return; }
      if (event.key !== 'Tab') return;
      const focusable = [toggle, ...menu.querySelectorAll('.menu__sheet a, .menu__sheet button')]
        .filter((el) => el.offsetParent !== null);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });

    menu.querySelectorAll('.menu__sheet a').forEach((a) => a.addEventListener('click', close));
    // Close when resizing up to the desktop layout
    matchMedia('(min-width: 1024px)').addEventListener('change', (e) => { if (e.matches) menu.open = false; });
  }

  /* ---------- Booking scheduler: load the iframe only on demand ---------- */
  document.querySelectorAll('[data-booking-embed]').forEach((box) => {
    let loaded = false;
    const load = () => {
      if (loaded) return;
      loaded = true;
      const frame = document.createElement('iframe');
      frame.src = box.dataset.src;
      frame.title = box.dataset.title;
      frame.loading = 'lazy';
      box.replaceChildren(frame);
    };
    const show = box.querySelector('[data-booking-show]');
    if (show) show.addEventListener('click', (event) => { event.preventDefault(); load(); focusFrame(box); });
    // Auto-load only when the visitor scrolls toward it. If it is already
    // in view when the page opens, wait for the click, so no third-party
    // script is part of the initial load.
    if ('IntersectionObserver' in window) {
      let first = true;
      const near = new IntersectionObserver(([entry]) => {
        const initial = first;
        first = false;
        if (entry.isIntersecting && !initial) { near.disconnect(); load(); }
      }, { rootMargin: '200px 0px' });
      near.observe(box);
    }
  });
  function focusFrame(box) { const f = box.querySelector('iframe'); if (f) f.focus(); }

  /* ---------- FAQ accordion: button + aria-expanded ---------- */
  document.querySelectorAll('[data-accordion]').forEach((box) => {
    box.classList.add('js-accordion');
    box.querySelectorAll('.faq__q').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false'); // open without JS, closed once we can toggle
      btn.addEventListener('click', () => {
        btn.setAttribute('aria-expanded', btn.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
      });
    });
  });

  /* ---------- Contact form honeypot: bots fill the hidden field ---------- */
  document.querySelectorAll('[data-honeypot-form]').forEach((form) => {
    form.addEventListener('submit', (event) => {
      const trap = form.querySelector('[data-honeypot]');
      if (trap && trap.value) event.preventDefault();
    });
  });
})();
